import { BaseService } from '$lib/api/services/utils/BaseService';
import type {
  IProfileListResponse,
  IProfileSearchParams,
  IUser,
  IUsernameValidationResponse,
} from '$utils/interfaces/users';
import type {
  ListFounderProfilesParams,
  ListFounderProfilesResponse,
} from '$shared/api/founders';
import type {
  AcceleratorProfileBody,
  EditProfileBody,
  EditProfileResponse,
  GetAcceleratorMembershipResponse,
  ListProfilesQuery,
  ListProfilesResponse,
  OnboardingBody,
  OnboardingResponse,
  RetrieveProfileCompletionQuery,
  RetrieveProfileCompletionResponse,
  RetrieveProfileQuery,
  RetrieveProfileResponse,
} from '$shared/api';

export class UserService extends BaseService {
  onboarding(body: OnboardingBody): Promise<OnboardingResponse> {
    return this.client.send('/api/users/onboarding', {
      method: 'POST',
      body,
    });
  }

  agreeToTerms() {
    return this.client.send('/api/users/agreement', {
      method: 'POST',
    });
  }

  update(body: EditProfileBody): Promise<EditProfileResponse> {
    return this.client.send('/api/users/profile', {
      method: 'PUT',
      body,
    });
  }

  usernameValidation(username?: string): Promise<IUsernameValidationResponse> {
    return this.client.send('/api/users/username/validate', {
      method: 'GET',
      query: { username },
    });
  }

  getOne(params?: { username?: string; userId?: string }): Promise<IUser> {
    return this.client.send('/api/users/profile', {
      method: 'GET',
      query: params,
    });
  }

  getAll(params: IProfileSearchParams): Promise<IProfileListResponse> {
    return this.client.send('/api/users/profiles', {
      method: 'GET',
      query: params,
    });
  }

  findFounders(
    params?: ListFounderProfilesParams,
  ): Promise<ListFounderProfilesResponse> {
    return this.client.send('/api/users/founders', {
      method: 'GET',
      query: params,
    });
  }

  acceleratorOnboarding(body: AcceleratorProfileBody) {
    return this.client.send('/api/accelerator/onboarding', {
      method: 'POST',
      body,
    });
  }

  updateAcceleratorProfile(body: AcceleratorProfileBody) {
    return this.client.send('/api/accelerator/profile', {
      method: 'PUT',
      body,
    });
  }

  getAcceleratorMembership(): Promise<GetAcceleratorMembershipResponse> {
    return this.client.send('/api/accelerator', {
      method: 'GET',
    });
  }

  // v2 profile endpoints
  retrieveProfile(
    query: RetrieveProfileQuery,
  ): Promise<RetrieveProfileResponse> {
    return this.client.send('/api/v2/users/profile', {
      method: 'GET',
      query,
    });
  }

  retrieveProfileCompletion(
    query: RetrieveProfileCompletionQuery,
  ): Promise<RetrieveProfileCompletionResponse> {
    return this.client.send('/api/users/profile/completion', {
      method: 'GET',
      query,
    });
  }

  listProfiles(query: ListProfilesQuery): Promise<ListProfilesResponse> {
    return this.client.send('/api/v2/users/profiles', {
      method: 'GET',
      query,
    });
  }
}
